import type { ReactNode } from 'react';
import { cn } from '@shared/lib/utils';
import { Card, CardContent, CardHeader } from '../layout/card';
import { HStack } from '../layout/stack';
import { Heading } from '../typography/heading';
import { Text } from '../typography/text';

type KpiCardProps = {
  title: string;
  titleAccessory?: ReactNode;
  value: number | string;
  trend?: ReactNode;
  subtext?: string;
  details?: Array<{
    label: string;
    value: number | string;
  }>;
  size?: 'sm' | 'md' | 'lg';
  locale?: string;
  className?: string;
  valueClassName?: string;
};

const sizeStyles = {
  sm: {
    title: 'text-xs',
    value: 'text-xl',
    subtext: 'text-xs',
  },
  md: {
    title: 'text-xs',
    value: 'text-2xl',
    subtext: 'text-xs',
  },
  lg: {
    title: 'text-sm',
    value: 'text-3xl',
    subtext: 'text-sm',
  },
} as const;

const formatValue = (value: number | string, locale?: string) => {
  if (typeof value === 'number') {
    return new Intl.NumberFormat(locale).format(value);
  }

  return value;
};

export function KpiCard({
  title,
  titleAccessory,
  value,
  trend,
  subtext,
  details,
  size = 'md',
  locale,
  className,
  valueClassName,
}: KpiCardProps) {
  const sizes = sizeStyles[size];
  const formattedValue = formatValue(value, locale);
  const shouldRenderMeta = Boolean(subtext || trend);

  return (
    <Card className={cn('h-full min-w-0 transition-shadow hover:shadow-md', className)}>
      <CardHeader className="pb-2">
        <HStack gap="xs" align="center" className="flex-wrap">
          <Text size="xs" className={cn('font-semibold text-foreground', sizes.title)}>
            {title}
          </Text>
          {titleAccessory}
        </HStack>
      </CardHeader>
      <CardContent className="space-y-1">
        <Heading as="h3" size="lg" className={cn('font-semibold', sizes.value, valueClassName)}>
          {formattedValue}
        </Heading>
        {shouldRenderMeta ? (
          <HStack gap="sm" align="center" className="min-w-0 flex-wrap">
            {subtext ? (
              <Text size="xs" color="muted" as="span" className={sizes.subtext}>
                {subtext}
              </Text>
            ) : null}
            {trend}
          </HStack>
        ) : null}
        {details && details.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 border-t border-border/60 pt-3">
            {details.map((detail) => (
              <div key={detail.label} className="space-y-1">
                <Text size="xs" color="muted" className="uppercase tracking-wide">
                  {detail.label}
                </Text>
                <Text size="sm" className="font-semibold text-foreground">
                  {formatValue(detail.value, locale)}
                </Text>
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
